import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box } from '@livable/saas-react-components';
import { Container, Tab, Tabs } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useTranslation } from 'react-i18next';
import HistoryTab from './HistoryTab';
import OverviewTab from './OverviewTab';
import { ReimbursementTab } from './ReimbursementTab';
import { ReimbursementTab as ReimbursementTabV2 } from './ReimbursementTabV2';
import { ReimbursementReceiptsCustomTab } from './ReimbursementReceiptsCustomTab';
import { UtilityAllocationExportTab } from './UtilityAllocationExportTab';
import { useStore } from 'src/stores';
import { useURLHandler } from 'src/utils/hooks';
import useReportingPageStyles from './styles';
import { tabsFor, reportsDisabledFor, isAppfolioFrom } from './tabsFor';

// observer, because both gates below (isAppfolio, disableReports) land asynchronously with the
// company fetch. Without it the tab list and the redirect keep their first-render values.
const ReportingPage = observer(() => {
  var _store$homeDashboardS, _store$homeDashboardS2;
  const classes = useReportingPageStyles();
  const store = useStore();
  const navigate = useNavigate();
  // const { t } = useTranslation();

  // Both stores carry the flag and hydrate independently - see isAppfolioFrom.
  const isAppfolio = isAppfolioFrom(store.company.companyFeatureStore.isAppfolio, (_store$homeDashboardS = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS === void 0 || (_store$homeDashboardS = _store$homeDashboardS.companyFeatureSet) === null || _store$homeDashboardS === void 0 ? void 0 : _store$homeDashboardS.isAppfolio);

  // Nothing renders until the flags are known. Both default to false, so an unhydrated AppFolio
  // customer reads as a regular one: tabsFor would list Accounts Receivable, effectiveTab would
  // default to it, and ReimbursementTabV2 would mount and fire AR requests they should never make
  // before the UI corrected itself. hasEverBeenRun flips on success OR failure, so a failed company
  // fetch still releases the page rather than hanging it.
  const flagsLoaded = store.company.getCurrentUserCompanyFromTokenAsync.hasEverBeenRun || Boolean(store.homeDashboardStore.getCurrentCompany);

  // QC HOLD: Reporting stays Livable-admin-only until prod QC passes, so it is verified before any
  // customer sees it. The customer-type rules below are the real gate - to go live, delete this
  // const and the `!isLivableAdmin ||` term, plus the matching pair in PmSidebar.
  const isLivableAdmin = store.account.userProfile.isUserAdmin || store.account.userProfile.isUserSuperAdmin;
  const reportsDisabled = !isLivableAdmin || reportsDisabledFor(store.company.companyFeatureStore.disableReports, (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.companyFeatureSet) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.disableReports, isAppfolio);
  useEffect(() => {
    if (flagsLoaded && reportsDisabled) {
      navigate('/active_dashboard', {
        replace: true
      });
    }
  }, [navigate, flagsLoaded, reportsDisabled]);
  const tabsNames = tabsFor(isAppfolio);

  // An AppFolio company has no Accounts Receivable tab, so it cannot be their default. Deep links
  // to a tab they cannot see fall back to the first one they can, rather than rendering nothing.
  const _useURLHandler = _objectSpread({}, useURLHandler(tabsNames[0], tabsNames)),
    handleSelectedTabChange = _useURLHandler.handleSelectedTabChange,
    selectedTab = _useURLHandler.selectedTab;
  const handleChange = (e, newValue) => handleSelectedTabChange(newValue);

  // isAppfolio arrives with the company fetch, AFTER useURLHandler has captured selectedTab from
  // the URL. So an AppFolio customer landing on ?tab=reimbursementV2 keeps a tab that has just
  // disappeared from under them. Fall back to the first tab they do have, and correct the URL.
  const effectiveTab = tabsNames.includes(selectedTab) ? selectedTab : tabsNames[0];
  useEffect(() => {
    if (effectiveTab !== selectedTab) {
      handleSelectedTabChange(effectiveTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveTab, selectedTab]);

  // After every hook, so the hook order never changes between renders.
  if (!flagsLoaded) return null;
  const getTab = () => {
    switch (effectiveTab) {
      case 'invoice-history':
        return /*#__PURE__*/React.createElement(HistoryTab, null);
      case 'reimbursement':
        return /*#__PURE__*/React.createElement(ReimbursementTab, null);
      case 'reimbursementV2':
        return /*#__PURE__*/React.createElement(ReimbursementTabV2, null);
      case 'reimbursementCustom':
        return /*#__PURE__*/React.createElement(ReimbursementReceiptsCustomTab, null);
      case 'utilityAllocationExport':
        return /*#__PURE__*/React.createElement(UtilityAllocationExportTab, null);
      case 'settings':
        return /*#__PURE__*/React.createElement("div", null, "settings");
      default:
      case 'overview':
        return /*#__PURE__*/React.createElement(OverviewTab, null);
    }
  };
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Tabs, {
    "aria-label": "tabs navigation",
    onChange: handleChange,
    position: "sticky",
    type: "navigation",
    value: effectiveTab
  }, !isAppfolio && /*#__PURE__*/React.createElement(Tab, {
    label: "Accounts Receivable",
    value: 'reimbursementV2',
    'custom-attribute': '752-ReportingPage-page-reimbursementV2'
  }), !isAppfolio && /*#__PURE__*/React.createElement(Tab, {
    label: "Reimbursement Receipts",
    value: 'reimbursementCustom'
  }), /*#__PURE__*/React.createElement(Tab, {
    label: "Utility Allocation Export",
    value: 'utilityAllocationExport'
  })), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement("div", {
    className: classes.lgSection
  }, getTab()))));
});
export default ReportingPage;